<script setup lang="ts">
/**
 * UConfirmDialog — 统一确认对话框基件 (SPEC_UI_UX_UPGRADE T1-3)
 * 替代 13 处原生 window.confirm（Tauri WebView 内原生框破坏宋韵视觉且阻塞主线程）。
 * - Promise API: const ok = await UConfirmDialog.confirm()
 * - 危险操作（danger）落朱砂印范式；焦点陷阱 + Esc 关闭 + 遮罩点击关闭
 * - a11y: role=dialog / aria-modal / 初始焦点落在取消键（安全默认）
 */
import { ref, onMounted, onBeforeUnmount, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { X } from '@lucide/vue';
import UButton from './UButton.vue';
import { pushOverlay } from '../../composables/overlayEscStack';

const emit = defineEmits<{ (e: 'result', ok: boolean): void }>();

const props = withDefaults(defineProps<{
  title?: string;
  message?: string;
  confirmText?: string;
  cancelText?: string;
  danger?: boolean;
}>(), {
  danger: false,
});

const { t } = useI18n({ useScope: 'global' });
const cancelBtn = ref<InstanceType<typeof UButton> | null>(null);
const panel = ref<HTMLElement | null>(null);

function close(ok: boolean) {
  emit('result', ok);
}

function onKeydown(e: KeyboardEvent) {
    // ESC 关闭已收归全局 overlayEscStack（window 捕获，后进先关，防叠层双关）
    // 此处仅保留焦点陷阱：Tab 循环限制在对话框内（a11y 基线 T2-4）
    if (e.key === 'Tab' && panel.value) {
    const focusables = panel.value.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    if (focusables.length === 0) { e.preventDefault(); return; }
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const active = document.activeElement;
    if (e.shiftKey && active === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && active === last) { e.preventDefault(); first.focus(); }
  }
}

let unregisterEsc: (() => void) | null = null;
onMounted(async () => {
    document.addEventListener('keydown', onKeydown, true);
    unregisterEsc = pushOverlay(() => close(false), 'uconfirm');
    await nextTick();
    cancelBtn.value?.$el?.focus?.();
});
onBeforeUnmount(() => {
    document.removeEventListener('keydown', onKeydown, true);
    unregisterEsc?.();
    unregisterEsc = null;
});

/** 命令式用法：在任意组件内
 *  const ok = await new Promise<boolean>(r => (confirmRequest.value = r));
 */
defineExpose({ close });
</script>

<template>
  <Teleport to="body">
    <div
      class="fixed inset-0 flex items-center justify-center bg-[rgba(0,0,0,0.45)] z-[var(--z-modal)]"
      @click.self="close(false)"
    >
      <div
        ref="panel"
        role="dialog"
        aria-modal="true"
        :aria-label="title || t('u_kit.confirm.default_title')"
        class="relative w-[min(400px,92vw)] bg-[var(--color-bg-primary)] border-[0.5px] border-[var(--color-border)] rounded-[2px] shadow-[var(--ds-shadow-overlay)] p-5 flex flex-col gap-3"
      >
        <button
          type="button"
          class="absolute top-2 right-2 p-1.5 rounded-[2px] text-[var(--color-text-muted)] hover:text-[var(--color-cinnabar-red)] hover:bg-[rgba(var(--color-cinnabar-red-rgb),0.08)] transition-colors outline-hidden focus-visible:ring-[0.5px] focus-visible:ring-[var(--color-ru-celadon)]/40"
          :title="t('common.close')"
          :aria-label="t('common.close')"
          @click="close(false)"
        >
          <X class="w-[14px] h-[14px]" :stroke-width="1.5" />
        </button>
        <div class="flex items-center gap-2.5 pr-8">
          <span v-if="danger" class="w-[20px] h-[20px] flex items-center justify-center bg-[var(--color-cinnabar-red)] rounded-[2px] shrink-0" aria-hidden="true">
            <span class="w-[8px] h-[8px] rounded-full bg-[var(--color-bg-primary)]" />
          </span>
          <h3 class="font-song text-[length:var(--ts-title)] text-[var(--color-text-primary)]">
            {{ title || t('u_kit.confirm.default_title') }}
          </h3>
        </div>
        <p class="text-[length:var(--ts-body)] text-[var(--color-text-secondary)] whitespace-pre-line">
          {{ message || t('u_kit.confirm.default_message') }}
        </p>
        <div class="flex justify-end gap-2 mt-2">
          <!-- data-testid 供渲染态判据使用：选品工作台 e2e 要断言"危险操作确认后才落状态"。
               没有稳定选择器时只能按文案匹配，而文案是多语言的 ⇒ 判据会随语言漂。 -->
          <UButton ref="cancelBtn" variant="ghost" size="sm" data-testid="u-confirm-cancel" @click="close(false)">
            {{ cancelText || t('u_kit.confirm.cancel') }}
          </UButton>
          <UButton :variant="danger ? 'danger' : 'primary'" size="sm" data-testid="u-confirm-ok" @click="close(true)">
            {{ confirmText || t('u_kit.confirm.ok') }}
          </UButton>
        </div>
      </div>
    </div>
  </Teleport>
</template>
